import { differenceInYears, format, isValid, parse, parseISO, startOfDay } from 'date-fns'
import { capitalize, isObjectLike } from 'lodash-es'

import { CustomerInfo } from '../../../models/customer/Customer'
import { User } from '../../../models/user/User'
import { DEPENDENT_VARIANT } from '../constants/constants'
import { CustomerName } from '../constants/customer'

export const formatDOB = (isoDate: string): string => {
  const dt = parseISO(isoDate)
  if (!isValid(dt)) return isoDate
  return format(dt, 'MM/dd/yyyy')
}

export const formatPronouns = (pronouns?: string[]): string =>
  Array.isArray(pronouns) && typeof pronouns[0] === 'string'
    ? (pronouns[0] as string)
        .split(',')
        .map((p) => capitalize(p.trim()))
        .join('/')
    : ''

// Keywords also include gender identity strings (e.g. 'male', 'female') so callers can pass
// either a pronoun array or a single-element gender array without a separate mapping.
const PRONOUN_PATTERNS = [
  { keywords: ['they', 'them', 'theirs'], possessive: 'their', subjective: 'they', reflexive: 'themselves' },
  { keywords: ['she', 'her', 'hers', 'female'], possessive: 'her', subjective: 'she', reflexive: 'herself' },
  { keywords: ['he', 'him', 'his', 'male'], possessive: 'his', subjective: 'he', reflexive: 'himself' },
  { keywords: ['ze', 'hir', 'hirs'], possessive: 'hir', subjective: 'ze', reflexive: 'hirself' },
  { keywords: ['just use my name', 'name'], possessive: 'their', subjective: 'they', reflexive: 'themselves' },
  { keywords: ['other'], possessive: 'their', subjective: 'they', reflexive: 'themselves' },
]

export const getPossessivePronoun = (pronouns?: string[]): string => {
  if (!pronouns?.length) return 'their'
  if (pronouns.length > 1) return 'their' // Multiple pronouns = use 'their'

  const pronoun = pronouns[0].toLowerCase()

  // Find matching pattern
  const match = PRONOUN_PATTERNS.find((pattern) => pattern.keywords.some((keyword) => pronoun.includes(keyword)))

  return match?.possessive || 'their'
}

export const getSubjectivePronoun = (pronouns?: string[]): string => {
  if (!pronouns?.length) return 'they'
  if (pronouns.length > 1) return 'they' // Multiple pronouns = use 'they'

  const pronoun = pronouns[0].toLowerCase()

  // Find matching pattern
  const match = PRONOUN_PATTERNS.find((pattern) => pattern.keywords.some((keyword) => pronoun.includes(keyword)))

  return match?.subjective || 'they'
}

export const getReflexivePronoun = (pronouns?: string[]): string => {
  if (!pronouns?.length) return 'themselves'
  if (pronouns.length > 1) return 'themselves' // Multiple pronouns = use 'themselves'

  const pronoun = pronouns[0].toLowerCase()

  // Find matching pattern
  const match = PRONOUN_PATTERNS.find((pattern) => pattern.keywords.some((keyword) => pronoun.includes(keyword)))

  return match?.reflexive || 'themselves'
}

export const isAgeTeen = (age: number) => {
  return age >= 13 && age <= 17
}

export const isAgeYoungerThanTeen = (age: number) => {
  return age < 13
}

export const getUserAge = (user: User | undefined) => {
  if (!user?.dob) {
    return undefined
  }

  const dobDate = parse(user.dob, 'MM/dd/yyyy', new Date())
  if (isNaN(dobDate.getTime())) {
    return undefined
  }

  const age = differenceInYears(new Date(), dobDate)
  return age
}

/** Filters linked children down to verified teens eligible for parent-led booking. */
export const getEligibleTeensForBooking = (children: User[]) =>
  children
    .filter((child) => {
      const age = getUserAge(child)
      return age !== undefined && isAgeTeen(age) && child.emailVerified === true
    })
    .map((child) => ({ lyraId: child.lyraId, firstName: child.firstname }))

export function sanitizeData(
  data: any,
  extraSensitiveFields: string[] = [],
  _ancestors: Set<object> = new Set(),
): string | object | any[] {
  const sensitiveFields = [
    'password',
    'passCode',
    'verifyCode',
    'token',
    'sessionToken',
    'id_token',
    'idToken',
    'refresh_token',
    'refreshToken',
    'access_token',
    'accessToken',
    'ssn',
    ...extraSensitiveFields,
  ] // Add more sensitive fields as needed

  // Sanitize arrays
  if (Array.isArray(data)) {
    if (_ancestors.has(data)) return '[Circular]'
    _ancestors.add(data)
    const result = data.map((item) => sanitizeData(item, extraSensitiveFields, _ancestors))
    _ancestors.delete(data)
    return result
  }

  if (isObjectLike(data)) {
    if (_ancestors.has(data)) return '[Circular]'
    _ancestors.add(data)

    const sanitizedData: any = {}

    // Use Object.keys or getOwnPropertyNames to handle non-plain objects
    Object.getOwnPropertyNames(data).forEach((key) => {
      if (sensitiveFields.some((field) => field.toLowerCase() === key.toLowerCase())) {
        sanitizedData[key] = '[REDACTED]'
      } else {
        sanitizedData[key] = sanitizeData(data[key], extraSensitiveFields, _ancestors)
      }
    })
    _ancestors.delete(data)
    return sanitizedData
  }

  // Sanitize strings using regular expressions
  if (typeof data === 'string') {
    let sanitizedString = data
    /**
     * This regex matches both single and double-quoted key-value pairs in an object-like string,
     * including handling escaped quotes.
     *
     * 1. [\'"]\\\\*: Match either a single quote (') or double quote (") at the start of the key,
     *    with optional backslashes (\\\\) to handle escaped quotes.
     * 2. ${field}: Dynamically insert the field name we're searching for.
     * 3. [\'"]\\\\*: Match the closing quote around the key, with optional backslashes (\\\\) to handle escaped quotes.
     * 4. \\s*: Allow for optional spaces around the colon (:).
     * 5. [\'"]\\\\*: Match either a single or double quote for the value, with optional backslashes (\\\\) to handle escaped quotes.
     * 6. ([^\'"]+): Capture the value inside the quotes (anything except single or double quotes).
     * 7. [\'"]\\\\*: Match the closing quote around the value, with optional backslashes (\\\\) to handle escaped quotes.
     *
     * Flags:
     * - 'g': Global search to match all instances.
     * - 'i': Case-insensitive search.
     */
    sensitiveFields.forEach((field) => {
      // JSON-style: "password": "value" or 'password': 'value'
      const jsonRegex = new RegExp(`['"]\\\\*${field}\\\\*['"]\\s*:\\s*['"]\\\\*([^'"]+)\\\\*['"]`, 'gi')
      sanitizedString = sanitizedString.replace(jsonRegex, `"${field}": "[REDACTED]"`)

      // URL-encoded style: password=value (for application/x-www-form-urlencoded)
      const urlEncodedRegex = new RegExp(`(^|&)${field}=[^&]*`, 'gi')
      sanitizedString = sanitizedString.replace(urlEncodedRegex, `$1${field}=[REDACTED]`)
    })

    return sanitizedString
  }

  // Return data as is for other types (e.g., numbers, booleans)
  return data
}

export const getDependentVariant = (age: number, email?: string | undefined) => {
  if (isAgeYoungerThanTeen(age)) {
    return DEPENDENT_VARIANT.CHILD
  } else if (isAgeTeen(age)) {
    return email ? DEPENDENT_VARIANT.SELF_MANAGED_TEEN : DEPENDENT_VARIANT.TEEN
  } else {
    return DEPENDENT_VARIANT.ADULT
  }
}

export const isDBTUser = (customerName?: string | null): boolean => {
  return customerName === CustomerName.DBT
}

export type DbtTeenStatus = {
  customerName?: string | null
  isDbtTeensEnabled?: boolean
  isUserTeen?: boolean
}

export type DbtTeenCareStatus = DbtTeenStatus & {
  episodeCount?: number
  appointmentCount?: number
  careProviderCount?: number
}

export type DbtTeenCareStatusResult = {
  isDbtTeen: boolean
  isDbtTeenInCare: boolean
}

export const getIsDbtTeen = ({ customerName, isDbtTeensEnabled, isUserTeen }: DbtTeenStatus): boolean =>
  isDBTUser(customerName) && !!isDbtTeensEnabled && !!isUserTeen

export const getDbtTeenCareStatus = ({
  episodeCount,
  appointmentCount,
  careProviderCount,
  ...dbtTeenStatus
}: DbtTeenCareStatus): DbtTeenCareStatusResult => {
  const isDbtTeen = getIsDbtTeen(dbtTeenStatus)
  const hasActiveCare = [episodeCount, appointmentCount, careProviderCount].some((count) => (count ?? 0) > 0)

  return {
    isDbtTeen,
    isDbtTeenInCare: isDbtTeen && hasActiveCare,
  }
}

export const getIsDbtTeenInCare = (dbtTeenCareStatus: DbtTeenCareStatus): boolean =>
  getDbtTeenCareStatus(dbtTeenCareStatus).isDbtTeenInCare

type DbtTeenLogoRouting = {
  isDbtTeenInCare: boolean
  isLoggedIn: boolean
  isCareManager: boolean
  isMember: boolean
}

export const getShouldRouteDbtTeenLogoToCare = ({
  isDbtTeenInCare,
  isLoggedIn,
  isCareManager,
  isMember,
}: DbtTeenLogoRouting): boolean => isDbtTeenInCare && isLoggedIn && !isCareManager && isMember

export const isLeidosUser = (customerName?: string | null): boolean => {
  return customerName === CustomerName.LEIDOS
}

export const isUphsUser = (customerName?: string | null): boolean => {
  return customerName === CustomerName.UPHS
}

export const isStarbucksUser = (customerName?: string | null): boolean => {
  return customerName === CustomerName.STARBUCKS
}

export const requiresPostMigrationConsent = ({
  isInternational,
  hipaaConsented,
  termsAndConditions,
  emailVerified,
}: {
  isInternational: boolean
  hipaaConsented: boolean
  termsAndConditions: boolean
  emailVerified: boolean
}): boolean => !isInternational && hipaaConsented === false && termsAndConditions === false && emailVerified === true

/**
 * A customer is terminated once today (browser-local) is on or after its last day of care,
 * compared at start-of-day — the last day of care itself is already blocked (NETEN-2468,
 * no advance cutoff). Missing/empty last_day_of_care means "not terminated": that is an
 * operational data gap, not a code bug, so the customer stays included.
 */
export const isTerminatedCustomer = (
  customer: Pick<CustomerInfo, 'last_day_of_care'>,
  now: Date = new Date(),
): boolean => {
  if (!customer.last_day_of_care) {
    return false
  }
  return startOfDay(now) >= startOfDay(parseISO(customer.last_day_of_care))
}
